import React from 'react';
import {
  CustomInputContainer,
  CustomInputLabel,
  ErrorMessage,
  CustomTextArea,
} from './CustomInputs.styles';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';

// Define the prop types for styled components
interface StyledInputProps {
  hasRightComponent?: boolean;
  hasLeftComponent?: boolean;
  hasError?: boolean;
  theme?: any;
}

// Add a new styled component for the modern text input
const ModernTextInput = styled.input<StyledInputProps>`
  padding: 14px 16px;
  padding-right: ${(props) => (props.hasRightComponent ? '100px' : '16px')};
  padding-left: ${(props) => (props.hasLeftComponent ? '64px' : '16px')};
  font-family: Manrope;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  height: 53px;
  color: ${({ theme }) => theme?.custom?.text || '#333'};
  border: 1px solid
    ${(props) =>
      props.hasError ? '#FF5C77' : props.theme?.custom?.stroke || '#e0e0e0'};
  outline: 0;
  border-radius: 8px;
  background-color: ${({ theme }) => theme?.custom?.background1 || 'white'};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s ease;
  width: 100%;

  &:focus {
    border-color: ${(props) =>
      props.hasError ? '#FF5C77' : props.theme?.custom?.primary || '#ff6a00'};
  }

  &::placeholder {
    color: ${({ theme }) => theme?.custom?.textLight || '#757575'};
    opacity: 0.7;
  }
`;

// Container to hold the input with left component
const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

interface Props {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (newValue: string) => void;
  errorMessage?: string;
  rightComponent?: React.ReactNode;
  leftComponent?: React.ReactNode;
  type?: 'TextInput' | 'TextArea';
}

const CustomTextInput: React.FC<Props> = ({
  label,
  onChange,
  placeholder,
  value,
  errorMessage,
  rightComponent,
  leftComponent,
  type = 'TextInput',
}) => {
  const theme = useTheme();

  const renderLabel = () => (
    <CustomInputLabel theme={theme as any}>{label}</CustomInputLabel>
  );

  const renderInput = () => {
    if (type === 'TextInput') {
      return (
        <InputContainer>
          {leftComponent && (
            <div
              style={{
                position: 'absolute',
                left: '8px',
                display: 'flex',
                alignItems: 'center',
                zIndex: 1,
                height: '100%',
              }}
            >
              {leftComponent}
            </div>
          )}
          <ModernTextInput
            theme={theme}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            hasRightComponent={!!rightComponent}
            hasLeftComponent={!!leftComponent}
            hasError={!!errorMessage}
          />
        </InputContainer>
      );
    }

    return (
      <CustomTextArea
        theme={theme as any}
        maxLength={255}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        hasError={!!errorMessage}
      />
    );
  };

  const renderRightComponent = () =>
    rightComponent && (
      <div
        style={{
          position: 'absolute',
          right: 16,
          top: '50%',
          transform: 'translateY(-50%)',
          color: theme.custom?.textLight || '#666',
          fontSize: '14px',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        {rightComponent}
      </div>
    );

  const renderError = () =>
    errorMessage && (
      <ErrorMessage theme={theme as any}>{errorMessage}</ErrorMessage>
    );

  return (
    <CustomInputContainer>
      <div>{renderLabel()}</div>
      {renderInput()}
      {renderRightComponent()}
      {renderError()}
    </CustomInputContainer>
  );
};

export default CustomTextInput;
